import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuthState } from "@/auth/AuthProvider";
import { buildSignInUrl } from "@/utils/auth";
import AvatarMenu from "@/components/nav/AvatarMenu";
import { Button } from "@/components/ui/button";

function ActaraLogoMark({ size = 26 }) {
  const uid = React.useId();
  const g1 = `actara_g1_${uid}`;
  const g2 = `actara_g2_${uid}`;
  const g3 = `actara_g3_${uid}`;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="Actara"
      style={{ display: "block" }}
    >
      <defs>
        <linearGradient id={g1} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#A5B4FF" />
          <stop offset="55%" stopColor="#7C6CFF" />
          <stop offset="100%" stopColor="#4F46E5" />
        </linearGradient>

        <linearGradient id={g2} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#B7C2FF" />
          <stop offset="45%" stopColor="#7E72FF" />
          <stop offset="100%" stopColor="#4338CA" />
        </linearGradient>

        <linearGradient id={g3} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#C7D2FE" />
          <stop offset="40%" stopColor="#8B5CF6" />
          <stop offset="100%" stopColor="#3730A3" />
        </linearGradient>
      </defs>

      <path
        d="M9 8.8H22.2C23.3 8.8 24.2 9.7 24.2 10.8V11.3C24.2 12.4 23.3 13.3 22.2 13.3H9C7.9 13.3 7 12.4 7 11.3V10.8C7 9.7 7.9 8.8 9 8.8Z"
        fill={`url(#${g1})`}
        opacity="0.75"
      />
      <path
        d="M7.2 14.2H20.4C21.5 14.2 22.4 15.1 22.4 16.2V16.7C22.4 17.8 21.5 18.7 20.4 18.7H7.2C6.1 18.7 5.2 17.8 5.2 16.7V16.2C5.2 15.1 6.1 14.2 7.2 14.2Z"
        fill={`url(#${g2})`}
        opacity="0.9"
      />
      <path
        d="M11 19.8H25C26.1 19.8 27 20.7 27 21.8V22.3C27 23.4 26.1 24.3 25 24.3H11C9.9 24.3 9 23.4 9 22.3V21.8C9 20.7 9.9 19.8 11 19.8Z"
        fill={`url(#${g3})`}
      />
    </svg>
  );
}

export default function AppHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isSignedIn, signOut } = useAuthState();

  const pathname = location.pathname || "/";
  const isDashboard = pathname === "/app";
  const isProjects = pathname.startsWith("/projects") || pathname.startsWith("/app/projects");

  const navClass = (active) =>
    `text-sm font-medium transition-colors ${
      active ? "text-indigo-600" : "text-slate-500 hover:text-slate-900"
    }`;

  const handleDashboard = () => {
    navigate("/");
  };

  const handleProjects = () => {
    if (!isSignedIn) {
      navigate(buildSignInUrl("/projects"));
      return;
    }
    navigate("/projects");
  };

  return (
    <nav className="sticky top-0 z-50 border-b border-slate-200 bg-white/95 backdrop-blur">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6 sm:px-8">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleDashboard}
            className="flex items-center gap-2 text-slate-900"
          >
            <ActaraLogoMark size={26} />
            <span className="text-lg font-semibold tracking-tight">Actara</span>
          </button>
        </div>

        <div className="hidden items-center gap-6 md:flex">
          <button type="button" onClick={handleProjects} className={navClass(isProjects)}>
            My Projects
          </button>
        </div>

        <div className="flex items-center gap-3">
          {isSignedIn ? (
            <AvatarMenu
              user={{
                fullName: user?.fullName || "",
                email: user?.primaryEmailAddress?.emailAddress || "",
                imageUrl: user?.imageUrl || "",
                publicMetadata: user?.publicMetadata || {},
              }}
              onSignOut={signOut}
            />
          ) : (
            <Button
              type="button"
              onClick={() => navigate(buildSignInUrl(pathname))}
              variant="default"
              className="rounded-lg"
            >
              Sign in
            </Button>
          )}
        </div>
      </div>
    </nav>
  );
}
